import { Autocomplete, FormControl, FormHelperText, FormLabel, Option, Select, Typography } from '@healthinal/ui';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { PostalCodeFields as PostalCodeEntry, usePostalCodeReference } from '../hooks/useAddressRef';
import { SingleLineFormControl } from './form/SingeLineFormComponents.tsx';

const INITIAL_ZIP_OPTIONS_LIMIT = 20;
const INITIAL_CITY_OPTIONS_LIMIT = 20;
const NUMERIC_POSTAL_CODE_REGEX = /^\d+$/;
const POSTAL_CODE_VALIDATION_MESSAGE = 'PLZ darf nur Zahlen enthalten';

type CityIndexEntry = { zips: Set<string>; cantons: Set<string> };
type CantonIndexEntry = { zips: Set<string>; cities: Set<string> };

function applySetFilters(options: string[], filters: Set<string>[]) {
  if (filters.length === 0) {
    return options;
  }

  return options.filter((option) => filters.every((filter) => filter.has(option)));
}

function isZipCompatibleWithCity(
  zip: string,
  zipEntry: PostalCodeEntry | undefined,
  cityEntry: CityIndexEntry | undefined,
) {
  return Boolean(zipEntry && zip && (!cityEntry || cityEntry.zips.has(zip)));
}

function isCityCompatibleWithCanton(
  city: string,
  cityEntry: CityIndexEntry | undefined,
  cantonEntry: CantonIndexEntry | undefined,
) {
  return Boolean(cityEntry && city && (!cantonEntry || cantonEntry.cities.has(city)));
}

interface PostalCodeFieldsProps<TFormValues extends FieldValues> {
  control: Control<TFormValues>;
  setValue: UseFormSetValue<TFormValues>;
  getValues: UseFormGetValues<TFormValues>;
  getFieldState?: UseFormGetFieldState<TFormValues>;
  zipName: Path<TFormValues>;
  cityName: Path<TFormValues>;
  cantonName: Path<TFormValues>;
  zipLabel?: string;
  cityLabel?: string;
  cantonLabel?: string;
  enabled?: boolean;
  layout?: 'default' | 'singleLine';
  variant?: 'plain' | 'outlined' | 'soft' | 'solid';
}

export function PostalCodeFields<TFormValues extends FieldValues>({
  control,
  setValue,
  getValues,
  getFieldState,
  zipName,
  cityName,
  cantonName,
  zipLabel = 'PLZ',
  cityLabel = 'Ort',
  cantonLabel = 'Kanton',
  enabled = true,
  layout = 'default',
  variant = 'plain',
}: PostalCodeFieldsProps<TFormValues>) {
  const zipValue = useWatch({
    control,
    name: zipName,
  });

  const cityValue = useWatch({
    control,
    name: cityName,
  });

  const cantonValue = useWatch({
    control,
    name: cantonName,
  });

  const [cityInputValue, setCityInputValue] = useState('');

  const normalizedZip = String(zipValue ?? '').trim();
  const normalizedCity = String(cityValue ?? '').trim();
  const normalizedCanton = String(cantonValue ?? '').trim();
  const deferredZip = useDeferredValue(normalizedZip);
  const deferredCityInput = useDeferredValue(cityInputValue.trim().toLocaleLowerCase());

  const { allZipOptions, allCityOptions, allCantonOptions, byCity, byCanton, getEntryByZip, isLoading } =
    usePostalCodeReference({
      enabled,
    });

  const zipEntry = getEntryByZip(normalizedZip);
  const cityEntry = byCity.get(normalizedCity);
  const cantonEntry = byCanton.get(normalizedCanton);

  const zipOptions = useMemo(() => {
    const filters: Set<string>[] = [];

    if (cityEntry && normalizedCity) {
      filters.push(cityEntry.zips);
    }

    if (cantonEntry && normalizedCanton) {
      if (!normalizedCity || isCityCompatibleWithCanton(normalizedCity, cityEntry, cantonEntry)) {
        filters.push(cantonEntry.zips);
      }
    }

    return applySetFilters(allZipOptions, filters);
  }, [allZipOptions, cityEntry, cantonEntry, normalizedCity, normalizedCanton]);

  const cityOptions = useMemo(() => {
    const filters: Set<string>[] = [];

    if (isZipCompatibleWithCity(normalizedZip, zipEntry, cityEntry)) {
      filters.push(new Set(zipEntry!.cities));
    }

    if (cantonEntry && normalizedCanton) {
      if (!normalizedCity || isCityCompatibleWithCanton(normalizedCity, cityEntry, cantonEntry)) {
        filters.push(cantonEntry.cities);
      }
    }

    return applySetFilters(allCityOptions, filters);
  }, [allCityOptions, zipEntry, cityEntry, cantonEntry, normalizedZip, normalizedCanton]);

  const cantonOptions = useMemo(() => {
    if (zipEntry && normalizedZip) {
      let cantons = [...zipEntry.cantons];

      if (cityEntry && normalizedCity && isZipCompatibleWithCity(normalizedZip, zipEntry, cityEntry)) {
        cantons = cantons.filter((canton) => cityEntry.cantons.has(canton));
      }

      return cantons.sort((a, b) => a.localeCompare(b));
    }

    if (cityEntry && normalizedCity) {
      return [...cityEntry.cantons].sort((a, b) => a.localeCompare(b));
    }

    return allCantonOptions;
  }, [allCantonOptions, cityEntry, zipEntry, normalizedZip, normalizedCity]);

  const visibleZipOptions = useMemo(() => {
    if (!deferredZip) {
      return zipOptions.slice(0, INITIAL_ZIP_OPTIONS_LIMIT);
    }

    return zipOptions.filter((zip) => String(zip).startsWith(deferredZip)).slice(0, INITIAL_ZIP_OPTIONS_LIMIT);
  }, [zipOptions, deferredZip]);

  const visibleCityOptions = useMemo(() => {
    if (!deferredCityInput) {
      return cityOptions.slice(0, INITIAL_CITY_OPTIONS_LIMIT);
    }

    return cityOptions
      .filter((city) => city.toLocaleLowerCase().includes(deferredCityInput))
      .slice(0, INITIAL_CITY_OPTIONS_LIMIT);
  }, [cityOptions, deferredCityInput]);

  const displayCantonOptions = useMemo(() => {
    if (!normalizedCanton || cantonOptions.includes(normalizedCanton)) {
      return cantonOptions;
    }

    return [normalizedCanton, ...cantonOptions];
  }, [cantonOptions, normalizedCanton]);

  useEffect(() => {
    setCityInputValue((current) => (current === normalizedCity ? current : normalizedCity));
  }, [normalizedCity]);

  // Keep the three fields mutually consistent: when the user picks a value in
  // one field, drop any sibling selection that no longer matches it. Without
  // this, an incompatible leftover (e.g. a prefilled PLZ from another canton)
  // would make the dependent option lists collapse to an empty intersection.
  const clearField = (name: Path<TFormValues>) =>
    setValue(name, '' as PathValue<TFormValues, typeof name>, {
      shouldDirty: true,
      shouldValidate: true,
    });

  const reconcileFromZip = (zip: string) => {
    const entry = getEntryByZip(zip);
    if (!entry) return;

    const currentCity = String(getValues(cityName) ?? '').trim();
    const currentCanton = String(getValues(cantonName) ?? '').trim();

    if (currentCity && !entry.cities.includes(currentCity)) clearField(cityName);
    if (currentCanton && !entry.cantons.includes(currentCanton)) clearField(cantonName);
  };

  const reconcileFromCity = (city: string) => {
    const entry = byCity.get(city.trim());
    if (!entry) return;

    const currentZip = String(getValues(zipName) ?? '').trim();
    const currentCanton = String(getValues(cantonName) ?? '').trim();

    if (currentZip && !entry.zips.has(currentZip)) clearField(zipName);
    if (currentCanton && !entry.cantons.has(currentCanton)) clearField(cantonName);
  };

  const reconcileFromCanton = (canton: string) => {
    const entry = byCanton.get(canton.trim());
    if (!entry) return;

    const currentZip = String(getValues(zipName) ?? '').trim();
    const currentCity = String(getValues(cityName) ?? '').trim();

    if (currentZip && !entry.zips.has(currentZip)) clearField(zipName);
    if (currentCity && !entry.cities.has(currentCity)) clearField(cityName);
  };

  const zipField = (
    <Controller
      name={zipName}
      control={control}
      rules={{
        validate: (value) => {
          const postalCode = String(value ?? '').trim();

          return !postalCode || NUMERIC_POSTAL_CODE_REGEX.test(postalCode) || POSTAL_CODE_VALIDATION_MESSAGE;
        },
      }}
      render={({ field, fieldState }) => (
        <>
          <Autocomplete
            freeSolo
            disabled={isLoading}
            options={visibleZipOptions}
            value={field.value ?? ''}
            inputValue={field.value ?? ''}
            onInputChange={(_, newInputValue, reason) => {
              // Only react to real user typing / clearing. MUI also fires this
              // with reason 'reset' on programmatic value syncs (e.g. after the
              // auto-save resets the field's default), which would otherwise
              // re-mark the field dirty and keep the dirty indicator alive.
              if (reason !== 'input' && reason !== 'clear') return;
              const nextZip = String(newInputValue ?? '');
              if (nextZip !== String(field.value ?? '')) {
                field.onChange(nextZip);
                reconcileFromZip(nextZip);
              }
            }}
            onChange={(_, newValue) => {
              const nextZip = typeof newValue === 'string' ? newValue : String(newValue ?? '');
              if (nextZip !== String(field.value ?? '')) {
                field.onChange(nextZip);
                reconcileFromZip(nextZip);
              }
            }}
            filterOptions={(items) => items}
            getOptionLabel={(option) => String(option)}
            placeholder={isLoading ? 'PLZ wird geladen...' : zipLabel}
            variant={variant}
          />
          {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
        </>
      )}
    />
  );

  const cityField = (
    <Controller
      name={cityName}
      control={control}
      render={({ field }) => (
        <Autocomplete
          freeSolo
          disabled={isLoading}
          options={visibleCityOptions}
          value={field.value ?? ''}
          inputValue={cityInputValue}
          onInputChange={(_, newInputValue, reason) => {
            if (reason !== 'input' && reason !== 'clear') return;
            const nextCity = String(newInputValue ?? '');
            setCityInputValue((current) => (current === nextCity ? current : nextCity));
            if (nextCity !== String(field.value ?? '')) {
              field.onChange(nextCity);
              reconcileFromCity(nextCity);
            }
          }}
          onChange={(_, newValue) => {
            const nextCity = typeof newValue === 'string' ? newValue : String(newValue ?? '');
            if (nextCity !== String(field.value ?? '')) {
              field.onChange(nextCity);
              setCityInputValue(nextCity);
              reconcileFromCity(nextCity);
            }
          }}
          filterOptions={(items) => items}
          getOptionLabel={(option) => String(option)}
          isOptionEqualToValue={(option, value) => option === value}
          placeholder={`${cityLabel} auswählen`}
          variant={variant}
        />
      )}
    />
  );

  const cantonField = (
    <Controller
      name={cantonName}
      control={control}
      render={({ field }) => (
        <Select
          disabled={isLoading}
          value={field.value || null}
          onChange={(_, value) => {
            const nextCanton = typeof value === 'string' ? value : '';
            if (nextCanton !== String(field.value ?? '')) {
              field.onChange(nextCanton);
              reconcileFromCanton(nextCanton);
            }
          }}
          placeholder={`${cantonLabel} auswählen`}
          variant={variant}>
          {displayCantonOptions.map((canton) => (
            <Option key={canton} value={canton}>
              {canton}
            </Option>
          ))}
        </Select>
      )}
    />
  );

  if (layout === 'singleLine') {
    if (!getFieldState) {
      throw new Error('PostalCodeFields: getFieldState is required when layout="singleLine".');
    }

    return (
      <>
        <SingleLineFormControl<TFormValues> label={zipLabel} fieldState={getFieldState(zipName)}>
          {zipField}
        </SingleLineFormControl>

        <SingleLineFormControl<TFormValues> label={cityLabel} fieldState={getFieldState(cityName)}>
          {cityField}
        </SingleLineFormControl>

        <SingleLineFormControl<TFormValues> label={cantonLabel} fieldState={getFieldState(cantonName)}>
          {cantonField}
        </SingleLineFormControl>
      </>
    );
  }

  return (
    <>
      <FormControl>
        <FormLabel>
          <Typography>{zipLabel}</Typography>
        </FormLabel>
        {zipField}
      </FormControl>

      <FormControl>
        <FormLabel>
          <Typography>{cityLabel}</Typography>
        </FormLabel>
        {cityField}
      </FormControl>

      <FormControl>
        <FormLabel>
          <Typography>{cantonLabel}</Typography>
        </FormLabel>
        {cantonField}
      </FormControl>
    </>
  );
}
